//---------------------------------------------------------
// Soft F2 — Admin deploy templates CRUD
//---------------------------------------------------------
import {Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
	SoftAlert,
	SoftIconAction,
	SoftPage,
	SoftSection,
	SoftSelect,
	SoftStack,
	SoftTable,
	SoftText,
	SoftTextField,
} from 'components/soft/SoftUI';
import {SOFT} from 'components/soft/softTheme';
import {
	createSoftTemplate,
	getSoftTemplate,
	listSoftTemplates,
	updateSoftTemplate,
} from 'connector/oam/softProvisionApi';
import {useRole} from 'hooks/query/oamHooks';
import {t} from 'i18next';
import {useCallback, useEffect, useState} from 'react';
import {SOFT_MODE, SOFT_OS_VARIANTS, SOFT_TEMPLATE_MODES, SoftMode, SoftTemplate} from 'types/soft_provision';

export default function TemplatesPage() {
	const {is_admin} = useRole();
	const [items, setItems] = useState<SoftTemplate[]>([]);
	const [err, setErr] = useState<string | null>(null);
	const [open, setOpen] = useState(false);
	const [editId, setEditId] = useState<string | null>(null);
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [osVariant, setOsVariant] = useState(SOFT_OS_VARIANTS[0]);
	const [mode, setMode] = useState<SoftMode>(SOFT_MODE);
	const [script, setScript] = useState('#!/bin/bash\n');

	const refresh = useCallback(async () => {
		try {
			setItems(await listSoftTemplates());
		} catch (e: any) {
			setErr(e?.message || 'list failed');
		}
	}, []);

	useEffect(() => {
		if (is_admin) refresh();
	}, [is_admin, refresh]);

	if (!is_admin) {
		return (
			<SoftPage>
				<SoftAlert severity="error">{t('You need an admin account for this page.')}</SoftAlert>
			</SoftPage>
		);
	}

	const openCreate = () => {
		setEditId(null);
		setName('');
		setDescription('');
		setOsVariant(SOFT_OS_VARIANTS[0]);
		setMode(SOFT_MODE);
		setScript('#!/bin/bash\n');
		setOpen(true);
	};

	const openEdit = async (id: string) => {
		const full = await getSoftTemplate(id);
		if (!full) {
			setErr('failed to load template');
			return;
		}
		setEditId(id);
		setName(full.name);
		setDescription(full.description || '');
		setOsVariant((full.osVariants?.[0] as any) || SOFT_OS_VARIANTS[0]);
		setMode(SOFT_TEMPLATE_MODES.some(m => m.id === full.mode) ? (full.mode as SoftMode) : SOFT_MODE);
		setScript(full.script || '');
		setOpen(true);
	};

	const onSave = async () => {
		setErr(null);
		if (editId) {
			const res = await updateSoftTemplate(editId, {
				name,
				mode,
				osVariants: [osVariant],
				script,
				description,
			});
			if (!res.ok) {
				setErr(res.error || 'update failed');
				return;
			}
		} else {
			const res = await createSoftTemplate({
				name,
				mode,
				osVariants: [osVariant],
				script,
				description,
			});
			if (!res.ok) {
				setErr(res.error || 'create failed');
				return;
			}
		}
		setOpen(false);
		await refresh();
	};

	return (
		<SoftPage
			title={t('Templates')}
			actions={
				<Button variant="contained" onClick={openCreate}>
					{t('Create')}
				</Button>
			}
		>
			<SoftSection>
				<SoftText>{t('Scripts that run when you provision a load balancer.')}</SoftText>
				{err && <SoftAlert severity="error">{err}</SoftAlert>}
				<SoftTable<SoftTemplate>
					label={t('Templates')}
					rows={items}
					rowKey={it => it.id}
					empty={t('No templates yet')}
					columns={[
						{key: 'name', header: t('Name'), render: it => it.name},
						{key: 'id', header: t('ID'), mono: true, render: it => it.id},
						{key: 'mode', header: t('Mode'), mono: true, render: it => it.mode},
						{key: 'os', header: t('OS variant'), mono: true, render: it => (it.osVariants || []).join(', ')},
						{
							key: 'actions',
							header: '',
							align: 'right',
							width: 64,
							render: it => <SoftIconAction label={t('Edit')} icon={<EditOutlinedIcon />} onClick={() => openEdit(it.id)} />,
						},
					]}
				/>
			</SoftSection>

			<Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
				<DialogTitle>{editId ? t('Edit template') : t('Create template')}</DialogTitle>
				<DialogContent>
					<SoftStack>
						<SoftTextField id="soft-template-name" label={t('Name')} value={name} onChange={e => setName(e.target.value)} />
						<SoftSelect
							id="soft-template-mode"
							label={t('Mode')}
							value={mode}
							onChange={v => setMode(v as SoftMode)}
							options={SOFT_TEMPLATE_MODES.map(m => ({value: m.id, label: m.label}))}
						/>
						<SoftSelect
							id="soft-template-os"
							label={t('OS variant')}
							value={osVariant}
							onChange={v => setOsVariant(v as any)}
							options={SOFT_OS_VARIANTS.map(v => ({value: v, label: <span style={{fontFamily: SOFT.font.mono}}>{v}</span>}))}
						/>
						<SoftTextField
							id="soft-template-description"
							label={t('Description')}
							value={description}
							onChange={e => setDescription(e.target.value)}
						/>
						<SoftTextField
							id="soft-template-script"
							label={t('Script')}
							value={script}
							onChange={e => setScript(e.target.value)}
							multiline
							minRows={SOFT.size.scriptRows}
							mono
							slotProps={{htmlInput: {spellCheck: false}}}
						/>
					</SoftStack>
				</DialogContent>
				<DialogActions>
					<Button variant="outlined" onClick={() => setOpen(false)}>
						{t('Cancel')}
					</Button>
					<Button variant="contained" onClick={onSave} disabled={!name.trim() || !script.trim()}>
						{t('Save')}
					</Button>
				</DialogActions>
			</Dialog>
		</SoftPage>
	);
}
